import { YesNoRadioGroupField } from "../../../../../Common/YesNoRadioGroup/YesNoRadioGroupField";
import {
  Box,
  Button,
  ComputedFieldProps,
  FormRow,
  FormSection,
  PlusIcon,
} from "@dbn/omnichannel-design-system-g4p";
import { useFormatMessage } from "../../../../../intl";
import { Option } from "fp-ts/Option";
import { Dispatch, SetStateAction } from "react";
import { constNull, pipe } from "fp-ts/function";
import { boolean, option } from "fp-ts";
import { useTenantCurrency } from "../../../../../Common/useTenantCurrency";
import { R39Field } from "./Fields/R39Field";
import { MoneyField } from "../../../../../Common/Price/MoneyField/MoneyField";
import { isOptionTrue } from "../../../domain";
import { Tooltip } from "../../../../../Common/Tooltip/Tooltip";
import { InfoSmallIcon } from "@g4p/react-styleguide-library";

type PropsCZ = {
  r39PartialTaxBaseFieldProps: ComputedFieldProps<Option<number>>;
  r201IncomeFromLeaseFieldProps: ComputedFieldProps<Option<number>>;
  line75ofTaxReturnFieldProps: ComputedFieldProps<Option<number>>;
  reInPersonalOwnershipFieldProps: ComputedFieldProps<Option<boolean>>;
  reUsedAsCollateralFieldProps: ComputedFieldProps<Option<boolean>>;
  reCoOwned: ComputedFieldProps<Option<boolean>>;
  incomeFromRentContractFieldProps: ComputedFieldProps<Option<number>>;
  isCooperativeOwnership: ComputedFieldProps<Option<boolean>>;
  isApplicantDeclareAsUserInDeclaration: ComputedFieldProps<Option<boolean>>;
  hasAdditionalIncome: ComputedFieldProps<Option<boolean>>;
  addInfoFromTaxReturn: boolean;
  setAddInfoFromTaxReturn: Dispatch<SetStateAction<boolean>>;
  isMainIncome: boolean;
  isEditing: boolean;
};

export function IncomeSectionCZ(props: PropsCZ) {
  const formatMessage = useFormatMessage();
  const currency = useTenantCurrency();

  const MIN_MONEY_VALIDATION = -999999999;
  const MAX_MONEY_VALIDATION = 999999999;

  const onAddInfoFromTaxReturn = () => {
    props.setAddInfoFromTaxReturn(true);
  };
  const onCloseAddInfoFromTaxReturnSection = () => {
    props.setAddInfoFromTaxReturn(false);
  };

  return (
    <FormSection>
      {props.isMainIncome && (
        <FormRow type="full">
          <YesNoRadioGroupField
            {...props.hasAdditionalIncome}
            label={formatMessage("StandardLoan.additionalIncome")}
            description={formatMessage(
              "StandardLoan.additionalIncome.description"
            )}
          />
        </FormRow>
      )}
      <FormRow type="full">
        <YesNoRadioGroupField
          {...props.reInPersonalOwnershipFieldProps}
          label={formatMessage(
            "StandardLoan.AdditionalIncome.IncomeSection.IsReInPersonalOwnershipLabel"
          )}
        />
      </FormRow>
      {pipe(
        props.reInPersonalOwnershipFieldProps.value,
        option.fold(
          constNull,
          boolean.fold(
            () => (
              <FormRow type="full">
                <YesNoRadioGroupField
                  {...props.isCooperativeOwnership}
                  label={formatMessage(
                    "StandardLoan.AdditionalIncome.IncomeSection.IsCooperativeOwnership"
                  )}
                />
              </FormRow>
            ),
            () => (
              <FormSection>
                <FormRow type="full">
                  <YesNoRadioGroupField
                    {...props.reCoOwned}
                    label={formatMessage(
                      "StandardLoan.AdditionalIncome.IncomeSection.IsReCoOwnedByAnotherPerson"
                    )}
                  />
                </FormRow>
                <FormRow type="full">
                  <YesNoRadioGroupField
                    {...props.reUsedAsCollateralFieldProps}
                    label={formatMessage(
                      "StandardLoan.AdditionalIncome.IncomeSection.IsReUsedCollateral"
                    )}
                  />
                </FormRow>
              </FormSection>
            )
          )
        )
      )}

      {pipe(
        !isOptionTrue(props.reInPersonalOwnershipFieldProps.value) &&
          isOptionTrue(props.isCooperativeOwnership.value),
        boolean.fold(constNull, () => (
          <FormRow type="full">
            <YesNoRadioGroupField
              {...props.isApplicantDeclareAsUserInDeclaration}
              label={formatMessage(
                "StandardLoan.AdditionalIncome.IncomeSection.IsApplicantDeclareAsUserInDeclaration"
              )}
            />
          </FormRow>
        ))
      )}
      {props.addInfoFromTaxReturn ? (
        <FormRow type="full">
          <Box>
            <Button
              label={formatMessage("Cancel")}
              variant="text"
              size="default"
              action={onCloseAddInfoFromTaxReturnSection}
              disabled={!props.isEditing}
            />
          </Box>
        </FormRow>
      ) : null}
      {!props.addInfoFromTaxReturn && [
        <FormRow type="full">
          <Box vAlignContent="center">
            <Button
              label={formatMessage(
                "StandardLoan.additionalIncome.addInformationTaxReturn"
              )}
              variant="text"
              action={onAddInfoFromTaxReturn}
              icon={PlusIcon}
              size="default"
              disabled={!props.isEditing}
            />
            <Tooltip
              content={formatMessage(
                "StandardLoan.additionalIncome.addInformationTaxReturn.description"
              )}
            >
              <InfoSmallIcon width={"14px"} height={"14px"} />
            </Tooltip>
          </Box>
        </FormRow>,
      ]}
      {props.addInfoFromTaxReturn && [
        <R39Field
          currency={currency}
          fieldProps={props.r39PartialTaxBaseFieldProps}
        />,
        <FormRow type="full">
          <MoneyField
            {...props.incomeFromRentContractFieldProps}
            currency={currency}
            label={formatMessage(
              "StandardLoan.AdditionalIncome.IncomeSection.incomeFromRentContractLabel"
            )}
            placeholder={formatMessage(
              "StandardLoan.AdditionalIncome.IncomeSection.incomeFromRentContractPlaceholder"
            )}
            min={MIN_MONEY_VALIDATION}
            max={MAX_MONEY_VALIDATION}
          />
        </FormRow>,
        <FormRow type="full">
          <MoneyField
            {...props.r201IncomeFromLeaseFieldProps}
            currency={currency}
            label={formatMessage(
              "StandardLoan.AdditionalIncome.IncomeSection.incomeOnlyFromLeaseLabel"
            )}
            placeholder={formatMessage(
              "StandardLoan.AdditionalIncome.IncomeSection.incomeOnlyFromLeasePlaceholder"
            )}
            min={MIN_MONEY_VALIDATION}
            max={MAX_MONEY_VALIDATION}
          />
        </FormRow>,
        <FormRow type="full">
          <MoneyField
            {...props.line75ofTaxReturnFieldProps}
            currency={currency}
            label={formatMessage(
              "StandardLoan.AdditionalIncome.IncomeSection.line75Label"
            )}
            placeholder={formatMessage(
              "StandardLoan.AdditionalIncome.IncomeSection.line75Placeholder"
            )}
            min={MIN_MONEY_VALIDATION}
            max={MAX_MONEY_VALIDATION}
          />
        </FormRow>,
      ]}
    </FormSection>
  );
}
